import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { ChevronLeft, LockKeyhole, Pencil } from 'lucide-react';
import { toast } from 'sonner';

import Button from '../../common/Button';
import { type TypeSharedModel } from '../../common/dataTypes';
import { ToastError, toastPromise } from '../../common/utils';
import { useGetProjectById } from '../../hooks';
import { useCloneModel } from '../../hooks/tanstack/useModelsHook';
import { useImportReceivedModel } from '../../hooks/tanstack/useReceivedModelsHooks';
import {
  useImportSharedModel,
  useRevokeSharedModel,
} from '../../hooks/tanstack/useSharedModelsHook';
import { useGetUser } from '../../hooks/tanstack/useUserHook';
import {
  useGetDistinguishModel,
  useGetRouteParams,
} from '../../hooks/tanstack/useUtilsHook';
import routes from '../../routes';
import { type ModelType } from '../../types';
import CollapseDescription from '../CollapseDescription';
import WaitingModelOutputDialog from '../WaitingModelOutputDialog';
import WarningDialog from '../WarningDialog';

import CreateModelFormDialog from './CreateModelFormDialog';
import ModelOutputTabs from './ModelOutputTabs';
import ShareModelFormDialog from './ShareModelFormDialog';
import ShareModelToHostFormDialog from './ShareModelToHostFormDialog';

const ModelOverview = () => {
  const [searchParams] = useSearchParams();

  const [openWaitingModelOutputDialog, setOpenWaitingModelOutputDialog] =
    useState(false);

  const { modelId, projectId, orgId } = useGetRouteParams();

  const navigate = useNavigate();

  const isActual = searchParams.get('isActualData') === 'true';

  const [openCreateModelDialog, setOpenCreateModelDialog] = useState(false);
  const [openRevokeAccessDialog, setOpenRevokeAccessDialog] = useState(false);

  const form = useForm();

  const { handleSubmit } = form;

  const type = searchParams.get('type') as ModelType;

  const {
    model,
    isDefault,
    isShared,
    isReceived,
    isRunningModelOutputServer,
    isFetchedModelOutput,
    runModelOutput,
  } = useGetDistinguishModel({
    isActual,
  });

  const { data: user } = useGetUser();

  const { data: project } = useGetProjectById(
    {
      projectId,
      orgId,
    },
    {
      enabled: !!projectId && !!orgId,
    }
  );

  const {
    mutateAsync: importReceiveModel,
    isPending: isImportingReceivedModel,
  } = useImportReceivedModel();

  const { mutateAsync: importSharedModel, isPending: isImportingSharedModel } =
    useImportSharedModel();

  const { mutateAsync: revokeSharedModel, isPending: isRevokingSharedModel } =
    useRevokeSharedModel();

  const { mutateAsync: cloneModel, isPending: isCloningModel } =
    useCloneModel();

  useEffect(() => {
    if (isFetchedModelOutput && !isRunningModelOutputServer) {
      setOpenWaitingModelOutputDialog(false);
    }
  }, [isFetchedModelOutput, isRunningModelOutputServer]);

  const handleOpenCreateModelDialog = () => {
    setOpenCreateModelDialog(true);
  };

  const handleCloseCreateModelDialog = () => {
    setOpenCreateModelDialog(false);
  };

  const handleGoBack = () => {
    navigate(
      `${routes.projects.model.index
        .replace(':org_id', orgId.toString())
        .replace(
          ':project_id',
          projectId.toString()
        )}${type === 'shared' ? '?type=shared' : type === 'received' ? '?type=received' : ''}`
    );
  };

  const handleImportModel = async () => {
    let res;

    if (isShared) {
      res = importSharedModel({
        orgId,
        projectId,
        modelId,
      });
    } else if (isReceived) {
      res = importReceiveModel({
        orgId,
        projectId,
        modelId,
      });
    }

    if (res) {
      const data = await res;

      toastPromise({
        promise: res,
        content: (
          <>
            Model imported successfully.{' '}
            <Link
              to={`/${orgId}/projects/${projectId}/models/${data.id}`}
              className="underline cursor-pointer text-sm"
            >
              Click here to go to the model.
            </Link>
          </>
        ),
      });
    }
  };

  const handleCloneModel = async () => {
    const res = cloneModel({
      orgId,
      projectId,
      modelId,
    });

    if (res) {
      const data = await res;

      toastPromise({
        promise: res,
        content: (
          <>
            Model cloned successfully.{' '}
            <Link
              to={`/${orgId}/projects/${projectId}/models/${data.id}`}
              className="underline cursor-pointer text-sm"
            >
              Click here to go to the model.
            </Link>
          </>
        ),
      });
    }
  };

  const handleRevokeAccess = async () => {
    await revokeSharedModel({
      orgId,
      projectId,
      modelId,
    });
    setOpenRevokeAccessDialog(false);
  };

  const onSubmit = async (data: any) => {
    const { aircrafts, airports } = data;

    const selectedAircrafts = Object.keys(aircrafts)
      .filter((a) => aircrafts[a])
      .flat();

    const selectedAirports = Object.keys(airports)
      .filter((a) => airports[a])
      .flat();

    if (selectedAircrafts.length === 0 && selectedAirports.length === 0) {
      toast.error(
        ToastError({
          message: 'Please select at least one aircraft and one airport.',
        })
      );
      return;
    }

    if (selectedAircrafts.length === 0) {
      toast.error(
        ToastError({
          message: 'Please select at least one aircraft.',
        })
      );
      return;
    }

    if (selectedAirports.length === 0) {
      toast.error(
        ToastError({
          message: 'Please select at least one airport.',
        })
      );
      return;
    }

    setOpenWaitingModelOutputDialog(true);

    runModelOutput({
      orgId,
      projectId,
      modelId,
      data: {
        filters: {
          aircrafts: selectedAircrafts,
          airports: selectedAirports,
        },
      },
    });
  };

  const isEditable = model && !model.is_base && isDefault && !model.is_locked;

  const isLoading =
    isImportingReceivedModel || isImportingSharedModel || isCloningModel;

  const isRevoked = !!model?.revoked_by;

  const isLocked = model?.is_locked ?? false;

  const isSharedProject =
    model?.shared_by !== undefined && model.shared_by?.id !== user?.id;

  const showActions = isShared || isReceived || (isSharedProject && isLocked);

  const isClone = isSharedProject && isLocked && !isShared && !isReceived;

  const actionHandler = isClone ? handleCloneModel : handleImportModel;
  const actionLabel = isClone ? 'Clone this model' : 'Import this model';

  return (
    <div className="flex flex-col gap-6 pb-6">
      <div className="flex flex-col gap-3">
        <div className="flex justify-between">
          <div className="flex items-center gap-4">
            <div
              onClick={handleGoBack}
              className="flex items-center w-full gap-4 cursor-pointer"
            >
              <ChevronLeft size={25} color="#333333" />
              <div className="flex flex-col w-fit">
                <div className="flex w-full items-center gap-2">
                  {(!isEditable || model.is_locked) && (
                    <LockKeyhole size={24} color="#333333" />
                  )}
                  <h1 className="text-2xl font-bold break-all">
                    {model?.name}
                  </h1>
                </div>
                {type === 'shared' && model && (
                  <div className="flex items-center text-[#666] gap-2">
                    <p>from {(model as TypeSharedModel).shared_by.full_name}</p>
                    <p>to {(model as TypeSharedModel).shared_with[0]?.email}</p>
                    <p>|</p>
                    <p>
                      {model.created_at
                        ? format(model.created_at, 'y/MM/dd')
                        : ''}
                    </p>
                    {isRevoked && (
                      <Tooltip
                        title={`Revoked by ${(model as TypeSharedModel).revoked_by?.full_name} on ${
                          model.revoked_at
                            ? format(model.revoked_at, 'y/MM/dd')
                            : ''
                        }`}
                      >
                        <p className="rounded-full font-semibold bg-[#FFECE8] text-[#B8341B] text-sm px-2 py-1">
                          Revoked
                        </p>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
            {isEditable && (
              <>
                <Pencil
                  size={20}
                  color="#333333"
                  className="cursor-pointer"
                  onClick={handleOpenCreateModelDialog}
                />
                <CreateModelFormDialog
                  open={openCreateModelDialog}
                  title={'Edit model'}
                  handleCloseDialog={handleCloseCreateModelDialog}
                  defaultValues={{
                    id: model.id,
                    name: model.name,
                    description: model.description,
                    dataset: model.dataset.id,
                  }}
                />
              </>
            )}
          </div>
          {project?.role === 'project_owner' &&
            !model?.is_locked &&
            !project?.is_guest_project && <ShareModelFormDialog />}
          {isDefault && !model?.is_locked && project?.is_guest_project && (
            <ShareModelToHostFormDialog />
          )}
          {showActions && model && (
            <div className="flex gap-2">
              {!isRevoked && !isReceived && !isLocked && (
                <Button
                  variant="outlined_red"
                  onClick={() => setOpenRevokeAccessDialog(true)}
                >
                  {isSharedProject ? 'Cancel share' : 'Revoke access'}
                </Button>
              )}
              <Button
                disabled={isLoading}
                loading={isLoading}
                variant="contained_blue"
                onClick={actionHandler}
              >
                {actionLabel}
              </Button>
            </div>
          )}
        </div>
        <CollapseDescription
          label="Description"
          description={model?.description}
          emptyText="No description provided"
        />
      </div>
      <div className="flex flex-col">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="flex items-center justify-between">
              <h2 className="text-3xl font-bold">Overview</h2>
            </div>
            <ModelOutputTabs />
          </form>
        </FormProvider>
      </div>
      <WarningDialog
        open={openRevokeAccessDialog}
        content={undefined}
        title="Are you sure you want to revoke this user's access"
        primaryActionText="Yes"
        secondaryActionText="Cancel"
        isPending={isRevokingSharedModel}
        showIcon={false}
        handleSecondaryAction={() => {
          setOpenRevokeAccessDialog(false);
        }}
        handlePrimaryAction={handleRevokeAccess}
      />
      <WaitingModelOutputDialog
        open={openWaitingModelOutputDialog}
        handleClose={() => {
          setOpenWaitingModelOutputDialog(false);
        }}
      />
    </div>
  );
};

export default ModelOverview;
